import { supabase } from "@/integrations/supabase/client";

export type SubscriptionStatus = {
  email: string;
  status: string;
  plan: string | null;
  price_id: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  /** True when the user has *current* paid/trial access. */
  active: boolean;
  /** True while inside a 14-day trial. */
  inTrial: boolean;
  /** Whole days remaining in trial (0 if not in trial). */
  daysLeftInTrial: number;
  /** True when this user's trial expired and they never added a payment method. */
  expiredTrial: boolean;
  /** True when the user cancelled but still has access until current_period_end. */
  cancelAtPeriodEnd: boolean;
};

const ACTIVE_STATUSES = new Set(["active", "trialing"]);

function daysUntil(iso: string | null): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/**
 * Read the subscription row for the given email. Returns null when no row
 * exists (e.g. anonymous lookup, or the user has never subscribed).
 *
 * RLS allows SELECT only when `auth.email() = email`, so this works for the
 * signed-in user looking up their own status.
 */
export async function getSubscriptionByEmail(
  email: string,
): Promise<SubscriptionStatus | null> {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return null;
  const { data, error } = await supabase
    .from("subscriptions")
    .select("email,status,plan,price_id,current_period_end,trial_end,cancel_at_period_end")
    .eq("email", trimmed)
    .maybeSingle();
  if (error || !data) return null;

  const periodOk =
    !data.current_period_end || new Date(data.current_period_end).getTime() > Date.now();
  const active = ACTIVE_STATUSES.has(data.status) && periodOk;
  const inTrial =
    data.status === "trialing" &&
    !!data.trial_end &&
    new Date(data.trial_end).getTime() > Date.now();

  return {
    email: data.email,
    status: data.status,
    plan: (data as any).plan ?? null,
    price_id: (data as any).price_id ?? null,
    current_period_end: data.current_period_end,
    trial_end: (data as any).trial_end ?? null,
    active,
    inTrial,
    daysLeftInTrial: inTrial ? daysUntil((data as any).trial_end) : 0,
    expiredTrial: (data as any).plan === "expired_trial",
    cancelAtPeriodEnd: !!(data as any).cancel_at_period_end,
  };
}

export function isActive(sub: SubscriptionStatus | null): boolean {
  return !!sub && sub.active;
}
